import type { Temporal } from '@js-temporal/polyfill'
import type {
  TPlayerWeeklyAvailabilityBlock,
  TPlayerWeeklyAvailabilitySchedule
} from '@lucimark/shared/models/player-weekly-availability'
import {
  isLocalWallClockInstantWithinPlayerWeeklyAvailability,
  msUntilNextPlayerWeeklyAvailabilityTransitionLocalWallClock
} from '@lucimark/shared/models/player-weekly-availability'

/** Short weekday labels (PT) — aligned with management app `adOrderWeekdayShort`. */
const WEEKDAY_SHORT_PT: Record<number, string> = {
  0: 'Dom',
  1: 'Seg',
  2: 'Ter',
  3: 'Qua',
  4: 'Qui',
  5: 'Sex',
  6: 'Sáb'
}

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

export function formatWeeklyAvailabilityBlockLinePt(block: TPlayerWeeklyAvailabilityBlock): string {
  const days = [...block.days]
    .sort((a, b) => a - b)
    .map(d => WEEKDAY_SHORT_PT[d] ?? String(d))
    .join(', ')
  return `${days}: ${pad2(block.start.hour)}:${pad2(block.start.minute)}–${pad2(block.end.hour)}:${pad2(block.end.minute)} (hora local)`
}

/** Human-readable duration until the next weekly window boundary. */
export function formatMsUntilWeeklyTransitionPt(ms: number): string {
  if (!Number.isFinite(ms) || ms < 1) {
    return 'menos de 1 min'
  }
  const totalMin = Math.max(1, Math.ceil(ms / 60_000))
  const h = Math.floor(totalMin / 60)
  const m = totalMin % 60
  if (h > 0 && m > 0) {
    return `~${h}h ${m}m`
  }
  if (h > 0) {
    return `~${h}h`
  }
  return `~${m} min`
}

export interface IPlayerWeeklyAvailabilityStatusDisplay {
  suppressed: boolean
  statusLabel: string
  lines: string[]
  nextTransitionHint: string | null
}

/**
 * Operator-facing summary for the Current State panel: configured windows, in/out status, and next change.
 */
/**
 * @param localWallClockAsUtc — device local wall clock encoded as UTC (player `getLocalAsUtcInstant()`).
 */
export function buildPlayerWeeklyAvailabilityStatusDisplay(
  schedule: TPlayerWeeklyAvailabilitySchedule | null,
  localWallClockAsUtc: Temporal.Instant
): IPlayerWeeklyAvailabilityStatusDisplay | null {
  if (schedule == null || schedule.blocks.length === 0) {
    return null
  }
  const suppressed = !isLocalWallClockInstantWithinPlayerWeeklyAvailability(
    schedule,
    localWallClockAsUtc
  )
  const lines = schedule.blocks.map(formatWeeklyAvailabilityBlockLinePt)
  const msUntil = msUntilNextPlayerWeeklyAvailabilityTransitionLocalWallClock(
    schedule,
    localWallClockAsUtc
  )
  let nextTransitionHint: string | null = null
  if (msUntil != null && msUntil > 0) {
    const duration = formatMsUntilWeeklyTransitionPt(msUntil)
    nextTransitionHint = suppressed
      ? `Reprodução retoma em ${duration}`
      : `Reprodução pausa em ${duration}`
  }
  return {
    suppressed,
    statusLabel: suppressed
      ? 'Fora da janela (ecrã preto)'
      : 'Dentro da janela',
    lines,
    nextTransitionHint
  }
}
